import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { InputPassword } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAddSendGridApi, useVerifySendGridApi } from "@/hooks/use-mailbox";
import { cn, extractErrorMessage } from "@/lib/utils";
import { VerifySendGridApiResponse } from "@/types/mailbox.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

const formSchema = z.object({
  apiKey: z.string().min(1, "API key is required"),
});

type FormData = z.infer<typeof formSchema>;

interface SendGridApiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const dialogData: Record<
  "form" | "result",
  { title: string; description?: string }
> = {
  form: {
    title: "SendGrid API Configuration",
    description: "You are connected to Sendgrid using the API key below",
  },
  result: {
    title: "SendGrid API Verification Result",
  },
};

export function SendGridApiDialog({
  open,
  onOpenChange,
}: SendGridApiDialogProps) {
  const [currentSlide, setCurrentSlide] = useState<"form" | "result">("form");

  const formDataRef = useRef<FormData | null>(null);
  const {
    mutateAsync: addSendGridApi,
    isPending: isAddPending,
    data: addData,
  } = useAddSendGridApi();

  const {
    mutateAsync: verifySendGridApi,
    isPending: isVerifyPending,
    data: verifyData,
  } = useVerifySendGridApi();

  const onVerify = async (data: FormData) => {
    try {
      formDataRef.current = data;
      await verifySendGridApi(data);
      // if (verifyData?.data?.success) {
        toast.success("SendGrid API settings have been verified.");
        onProceed();
        setCurrentSlide("result");
      // } else {
      //   toast.error("Invalid API key. Please check your SendGrid API key and try again.");
      //   setCurrentSlide("form");
      // }
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Failed to verify SendGrid API settings."
      );
      toast.error(message);
    }
  };

  const onProceed = async () => {
    try {
      if (!formDataRef.current) {
        throw new Error("Form data is not available");
      }

      await addSendGridApi(formDataRef.current);
      toast.success("SendGrid API settings have been added.");
      onOpenChange(false);
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Failed to add SendGrid API settings."
      );
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{dialogData[currentSlide].title}</DialogTitle>
          {dialogData[currentSlide].description && (
            <DialogDescription>
              {dialogData[currentSlide].description}
            </DialogDescription>
          )}
        </DialogHeader>

          <SendGridApiForm onSubmit={onVerify} isPending={isVerifyPending} />


        {/* {currentSlide === "result" && (
          <SendGridApiResult
            data={data}
            onProceed={onProceed}
            onCancel={() => onOpenChange(false)}
            isPending={isAddPending}
          />
        )} */}
      </DialogContent>
    </Dialog>
  );
}

const SendGridApiForm = ({
  onSubmit,
  isPending,
}: {
  onSubmit: (data: FormData) => void;
  isPending: boolean;
}) => {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      apiKey: "",
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-3"
        autoComplete="off"
      >
        <FormField
          control={form.control}
          name="apiKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>API key *</FormLabel>
              <FormControl>
                <InputPassword
                  {...field}
                  placeholder="Enter your API key"
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Alert variant="info" className="rounded-lg">
          <AlertDescription className="text-sm">
            <strong>Note:</strong> We prioritize your privacy. All API key
            details are securely stored and fully encrypted to ensure your
            information is protected at all times.
          </AlertDescription>
        </Alert>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              form.reset();
              // onCancel();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Saving..." : "Save Configuration"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

// const SendGridApiResult = ({
//   data,
//   onProceed,
//   isPending,
//   onCancel,
// }: {
//   data: VerifySendGridApiResponse | undefined;
//   onProceed: () => void;
//   onCancel: () => void;
//   isPending: boolean;
// }) => {
//   if (!data)
//     return (
//       <div className="text-center text-sm text-muted-foreground py-4">
//         No data found
//       </div>
//     );

//   return (
//     <div className="space-y-4">
//       <div className="text-sm">
//         <span className="font-medium">Domains found:</span>{" "}
//         {data.domains?.length ?? 0}
//       </div>
//       <div className="text-sm">
//         <span className="font-medium">
//           Senders: {data.senders?.length ?? 0}
//         </span>
//         {Array.isArray(data.senders) && data.senders.length > 0 ? (
//           <>
//             <Alert variant="info" className="rounded-lg mb-2">
//               <AlertTitle className="text-sm">Note:</AlertTitle>
//               <AlertDescription className="text-sm">
//                 Only verified senders will be added.
//               </AlertDescription>
//             </Alert>
//             <ScrollArea className="h-[200px]">
//               <ul className="list-decimal list-inside mt-1 space-y-1">
//                 {data.senders?.map((sender, idx) => (
//                   <li key={sender.id + idx} className="flex items-center gap-1">
//                     <span className="text-muted-foreground text-xs">
//                       {idx + 1}.
//                     </span>
//                     <span className="font-mono">{sender.from.name}</span>
//                     <span className="text-muted-foreground text-wrap">
//                       {sender.from.email ? ` (${sender.from.email})` : ""}
//                     </span>
//                     <span
//                       className={cn(
//                         "text-muted-foreground text-xs",
//                         sender.verified.status
//                           ? "text-green-500"
//                           : "text-red-500"
//                       )}
//                     >
//                       {sender.verified.status ? "Verified" : "Unverified"}
//                     </span>
//                   </li>
//                 ))}
//               </ul>
//             </ScrollArea>
//           </>
//         ) : (
//           <div className="text-muted-foreground mt-1">
//             No senders are available. You must have at least one sender to
//             proceed.
//           </div>
//         )}
//       </div>

//       <div className="flex justify-end space-x-3 pt-4">
//         <Button type="button" variant="outline" onClick={() => onCancel()}>
//           Cancel
//         </Button>
//         {data.senders.length > 0 && (
//           <Button
//             type="button"
//             variant="default"
//             onClick={onProceed}
//             disabled={isPending}
//           >
//             {isPending ? "Proceeding..." : "Proceed"}
//           </Button>
//         )}
//       </div>
//     </div>
//   );
// };
